/* eslint-disable @typescript-eslint/no-unused-vars, no-unused-vars */
import { useNumberMask } from '../index.js';
import type { UseNumberMaskT } from '../index.js';

// test we expose the namespace and the namespace follows our deliverable conventions
type ComponentPropsForApp = UseNumberMaskT.Props;
type ComponentPropsInternals = UseNumberMaskT.InternalProps;
type ComponentPropsDefaultProps = UseNumberMaskT.DefaultProps;
type ComponentPropsOptionalProps = UseNumberMaskT.OptionalProps;
type ComponentPropsRequiredProps = UseNumberMaskT.RequiredProps;

const testRequiredProps: ComponentPropsRequiredProps = {
  valueSetter: () => {},
};

const testOptionalProps: ComponentPropsOptionalProps = {
  max: 10000,
  min: 1,
};

// difference Props and InternalProps is that InternalProps has all the default props filled in
// Props allows for partial defaults
const testPartialDefaults: Partial<ComponentPropsDefaultProps> = {
  onChange: () => {},
  onKeyDown: () => {},
  decimalPlaces: 2,
  allowNegative: true,
};

const testProps: ComponentPropsForApp = {
  ...testRequiredProps,
  ...testOptionalProps,
  ...testPartialDefaults,
};

const testPropsAsSyntax = {
  ...testRequiredProps,
  ...testOptionalProps,
  ...testPartialDefaults,
} as ComponentPropsForApp;

// InternalProps requires all defaults to be filled in
const testCompleteDefaults: Required<ComponentPropsDefaultProps> = {
  onChange: () => {},
  onKeyDown: () => {},
  onBlur: () => {},
  suffix: '',
  prefix: '',
  decimalPlaces: 2,
  allowNegative: false,
  includeThousandsSeparator: true,
  decimalRequired: false,
};

const testInternalProps: ComponentPropsInternals = {
  ...testRequiredProps,
  ...testOptionalProps,
  ...testCompleteDefaults,
};

const testInternalPropsAsSyntax = {
  ...testRequiredProps,
  ...testOptionalProps,
  ...testCompleteDefaults,
} as ComponentPropsInternals;

// using the explicit type definition, if there is an error, it will be marked on the key that is wrong
const testExplicitDefinition: ComponentPropsForApp = {
  valueSetter: () => {},
  onChange: () => {},
  onKeyDown: () => {},
  onBlur: () => {},
  suffix: '',
  prefix: '',
  decimalPlaces: 2,
  allowNegative: false,
  includeThousandsSeparator: true,
  decimalRequired: false,
  max: 10000,
  min: 1,
};

// using the "as" syntax, if there is an error, it will be marking the whole object as wrong because it is not compatible with the type
const testInferedTypeCompatibility = {
  valueSetter: () => {},
  onChange: () => {},
  onKeyDown: () => {},
  onBlur: () => {},
  suffix: '',
  prefix: '',
  decimalPlaces: 2,
  allowNegative: false,
  includeThousandsSeparator: true,
  decimalRequired: false,
  max: 10000,
  min: 1,
} as ComponentPropsForApp;

const testDefinitionAsConst = {
  valueSetter: () => {},
  onChange: () => {},
  onKeyDown: () => {},
  onBlur: () => {},
  suffix: '',
  prefix: '',
  decimalPlaces: 2,
  allowNegative: false,
  includeThousandsSeparator: true,
  decimalRequired: false,
  max: 10000,
  min: 1,
} as const;

const ExampleUsageComponent = () => {
  // works with explicitly casted props, all syntaxes
  const inputProps = useNumberMask({
    ...testExplicitDefinition,
  });
  const inputProps2 = useNumberMask({
    ...testInferedTypeCompatibility,
  });
  const inputProps3 = useNumberMask({
    ...testDefinitionAsConst,
  });
  // works with inline values
  const { onChange, onKeyDown, onBlur } = useNumberMask({
    valueSetter: () => {},
    onChange: () => {},
    onKeyDown: () => {},
    onBlur: () => {},
    suffix: '',
    prefix: '',
    decimalPlaces: 2,
    allowNegative: false,
    includeThousandsSeparator: true,
    decimalRequired: false,
    max: 10000,
    min: 1,
  });

  return <div></div>;
};
